// src/hooks/useSharedItem.js
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseItemShareParam, searchWithoutItemParam } from '../utils/itemShare';

/**
 * The "which item is expanded" state for a shared link (?item=…).
 *
 * Deliberately DERIVED from the URL on every render instead of stored: the
 * recipient of a link arrives with none of the sender's state, so anything we
 * kept in React would have to be rebuilt — and could drift between the mobile
 * layout, the desktop sidebar and the browser Back button. Reading the URL
 * means a cold load, a refresh, a restored tab and a remounted list all show
 * the same thing.
 *
 * Only usable inside the router.
 *
 * `closeShared` is safe to call unconditionally from any close/tab-change
 * handler: it no-ops when no shared link is open. It replaces the history
 * entry so Back leaves the app rather than reopening the item just dismissed.
 */
export const useSharedItem = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const sharedId = parseItemShareParam(location.search);

  const closeShared = useCallback(() => {
    if (!parseItemShareParam(window.location.search)) return;
    navigate(
      { pathname: window.location.pathname, search: searchWithoutItemParam(window.location.search) },
      { replace: true }
    );
  }, [navigate]);

  // Stable seed for EventExpandedView — it only needs the id and fetches the
  // rest itself. A fresh object each render would churn its props for nothing.
  const sharedPlace = useMemo(
    () => (sharedId ? { id: sharedId, __sharedLink: true } : null),
    [sharedId]
  );

  return { sharedId, sharedPlace, closeShared };
};

export default useSharedItem;
